import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Segment,
  Container,
  Header,
  Menu,
  Input,
  Divider,
} from "semantic-ui-react";
import {
  faArrowRight,
  faPaperclip,
  faPhone,
  faPhoneSlash,
  faVideoCamera,
  faVideoSlash,
  faVolumeHigh,
  faVolumeMute,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useParams, useSearchParams } from "react-router-dom";
import Peer from "peerjs";
import ServiceNotification from "../../core/services/services.notification";
import { useTurnCredentials } from "../../utils/useTurnCredentials";
import { useUserStore } from "../../store/useUserStore";

const CHUNK_SIZE = 16 * 1024;
const HOST_POLL_INTERVAL = 3000;
const HOST_MAX_ATTEMPTS = 20;
let hostPollTimer = null;
let hostAttempt = 0;

const ChatRoom = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();

  const token = localStorage.getItem("hsb_token");

  const isHost = searchParams.get("host") === "true";
  const user = useUserStore((state) => state.user);
  const iceServers = useTurnCredentials();

  const peerRef = useRef(null);
  const connRef = useRef(null);
  const callRef = useRef(null);
  const localStreamRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const incomingFileRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const reconnectRef = useRef(false);
  const MAX_RETRIES = 3;
  const retryCountRef = useRef(0);

  const userNameRef = useRef(null);

  const [theirName, setTheirName] = useState(null);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`chat_${roomId}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [input, setInput] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const [sendingFile, setSendingFile] = useState(null);
  const [receivingFile, setReceivingFile] = useState(null);

  const [callStatus, setCallStatus] = useState("idle");
  const [callType, setCallType] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCamOff, setIsCamOff] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(`chat_${roomId}`, JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    let destroyed = false;

    if (userNameRef.current == null) {
      if (user?.nickName) {
        userNameRef.current = user?.nickName;
      } else if (localStorage.getItem("nickName")) {
        userNameRef.current = localStorage.getItem("nickName");
      } else {
        let name = prompt("نام خود را وارد کنید");
        localStorage.setItem("nickName",name)
        userNameRef.current = name;
      }
    }

    const tryConnectToHost = (peer, getDestroyed) => {
      if (getDestroyed || destroyed) return;
      if (!peer || peer.destroyed) return;

      hostAttempt++;

      if (hostAttempt > HOST_MAX_ATTEMPTS) {
        setStatus("error");
        setErrorMessage("Host not found. Please check the room ID.");
        return;
      }

      setStatus("waiting_for_host"); // 👈 new status to show the user

      const conn = peer.connect(roomId, { reliable: true });

      // PeerJS doesn't give a clean "host not found" error on connect()
      // Instead the connection just never opens — detect this via timeout
      let didOpen = false;

      conn.on("open", () => {
        didOpen = true;
        hostAttempt = 0;
        clearTimeout(hostPollTimer);
        connRef.current = conn;
        setStatus("connected");
        conn.send({ type: "handshake", name: userNameRef.current });
        setupConnection(conn);
      });

      conn.on("error", (err) => {
        console.warn("Connection attempt error:", err);
        if (!didOpen) {
          scheduleHostRetry(peer);
        }
      });

      // If no "open" within timeout, host isn't ready yet
      hostPollTimer = setTimeout(() => {
        if (!didOpen && !destroyed) {
          console.log(
            `Host not ready, retrying... (${hostAttempt}/${HOST_MAX_ATTEMPTS})`
          );
          scheduleHostRetry(peer);
        }
      }, 4000); // wait 4s for conn.on("open") before giving up
    };

    const scheduleHostRetry = (peer) => {
      if (destroyed || !peer || peer.destroyed) return;

      ServiceNotification.show(
        "",
        `در انتظار میزبان... (${hostAttempt}/${HOST_MAX_ATTEMPTS})`,
        "warning"
      );

      hostPollTimer = setTimeout(() => {
        tryConnectToHost(peer, false);
      }, HOST_POLL_INTERVAL);
    };

    const initPeer = () => {
      if (!iceServers) return; // Only gate on iceServers, not on status

      const iceConfig = { config: { iceServers } };

      // Clean up existing peer before creating a new one
      if (peerRef.current && !peerRef.current.destroyed) {
        peerRef.current.destroy();
      }

      const peer = isHost
        ? new Peer(roomId, iceConfig)
        : new Peer(undefined, iceConfig);

      peerRef.current = peer;
      setStatus("connecting");

      peer.on("open", () => {
        if (destroyed) return;
        retryCountRef.current = 0;
        reconnectRef.current = true;

        if (isHost) {
          setStatus("waiting");
        } else {
          tryConnectToHost(peer, destroyed);
        }
      });

      peer.on("connection", (conn) => {
        connRef.current = conn;

        conn.on("open", () => {
          setStatus("connected");
          conn.send({ type: "handshake", name: userNameRef.current }); // 👈 send here
        });
        setupConnection(conn);
      });

      peer.on("call", (call) => {
        const type = call.metadata?.type ?? "voice";
        setIncomingCall(call);
        setCallType(type);
        setCallStatus("receiving");
      });

      peer.on("disconnected", () => {
        if (destroyed) return;
        console.log("Peer disconnected, scheduling reconnect...");

        if (retryCountRef.current >= MAX_RETRIES) {
          setStatus("error");
          setErrorMessage("Max retries reached");
          return;
        }
        const delay = 1500;
        // * retryCountRef.current;

        setTimeout(() => {
          if (destroyed) return;
          if (peerRef.current && !peerRef.current.destroyed) {
            peerRef.current.reconnect();
          } else {
            initPeer();
          }
        }, delay);
      });

      peer.on("error", (err) => {
        if (destroyed) return;
        console.error("PeerJS error type:", err.type);
        console.error("PeerJS error message:", err.message);

        if (err.type === "unavailable-id") {
          // ServiceNotification.show("", "تلاش مجدد برای برقراری ارتباط ...", "warning");
          if (retryCountRef.current >= MAX_RETRIES) return;
          // retryCountRef.current++;
          const delay = 3000;
          // * retryCountRef.current;

          setTimeout(() => {
            if (!destroyed) initPeer();
          }, delay);

          return;
        }

        setStatus("error");
        setErrorMessage(err.message);
      });
    };

    initPeer(); // Always call directly — no status gate here

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !destroyed) {
        if (peerRef.current?.disconnected && !peerRef.current?.destroyed) {
          console.log("Page visible again, reconnecting...");
          ServiceNotification.show(
            "",
            "تلاش مجدد برای برقراری ارتباط ...",
            "warning"
          );
          retryCountRef.current = 0;
          peerRef.current.reconnect(); // Prefer reconnect() over full reinit
        } else if (!peerRef.current || peerRef.current.destroyed) {
          retryCountRef.current = 0;
          initPeer();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      destroyed = true;
      clearTimeout(hostPollTimer); // 👈 add this
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopLocalStream();
      if (peerRef.current && !peerRef.current.destroyed) {
        peerRef.current.destroy();
      }
    };
  }, [roomId, iceServers]);

  // isHost, iceServers, iceServersKey
  useEffect(() => {
    const handleUnload = () => {
      connRef.current?.close();
      peerRef.current?.destroy();
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (localStreamRef.current && localVideoRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [callStatus]);

  useEffect(() => {
    if (callStatus !== "ongoing" || !remoteStreamRef.current) return;

    const stream = remoteStreamRef.current;
    const type = callRef.current?.metadata?.type ?? callType;

    setTimeout(() => {
      if (type === "video") {
        attachStream(remoteVideoRef, stream);
      }
      attachStream(remoteAudioRef, stream);
    }, 100);
  }, [callStatus]);

  const setupConnection = (conn) => {
    conn.on("data", (data) => {
      if (data?.type === "handshake") {
        setTheirName(data.name);
        return;
      }
      if (data?.type === "message") {
        setMessages((prev) => [
          ...prev,
          { kind: "text", from: "them", text: data.text },
        ]);
        return;
      }

      if (data?.type === "call-rejected") {
        callRef.current?.close();
        callRef.current = null;
        stopLocalStream();
        setCallStatus("idle");
        setCallType(null);
        return;
      }

      if (data?.type === "call-ended") {
        callRef.current?.close();
        callRef.current = null;
        stopLocalStream();
        setCallStatus("idle");
        setCallType(null);
        setIsMuted(false);
        setIsCamOff(false);
        return;
      }

      if (data?.type === "file-start") {
        incomingFileRef.current = {
          name: data.name,
          size: data.size,
          fileType: data.fileType,
          chunks: [],
          received: 0,
        };
        setReceivingFile({ name: data.name, progress: 0 });
        return;
      }
      if (data?.type === "file-chunk") {
        const inc = incomingFileRef.current;
        if (!inc) return;
        inc.chunks.push(data.chunk);
        inc.received += data.chunk.byteLength;
        setReceivingFile({
          name: inc.name,
          progress: Math.round((inc.received / inc.size) * 100),
        });
        return;
      }
      if (data?.type === "file-end") {
        const inc = incomingFileRef.current;
        if (!inc) return;
        const blob = new Blob(inc.chunks, { type: inc.fileType });
        const url = URL.createObjectURL(blob);
        setMessages((prev) => [
          ...prev,
          {
            kind: "file",
            from: "them",
            name: inc.name,
            size: inc.size,
            fileType: inc.fileType,
            url,
          },
        ]);
        incomingFileRef.current = null;
        setReceivingFile(null);
        return;
      }
    });

    conn.on("open", () => {
      setStatus("connected");
      conn.send({ type: "handshake", name: userNameRef.current });
    });

    conn.on("close", () => setStatus("disconnected"));
  };

  const sendMessage = () => {
    if (!input.trim() || !connRef.current) return;
    connRef.current.send({ type: "message", text: input });
    setMessages((prev) => [...prev, { kind: "text", from: "me", text: input }]);
    setInput("");
  };

  const sendFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !connRef.current) return;

    setSendingFile({ name: file.name, progress: 0 });

    connRef.current.send({
      type: "file-start",
      name: file.name,
      size: file.size,
      fileType: file.type,
    });

    const buffer = await file.arrayBuffer();
    let offset = 0;

    while (offset < buffer.byteLength) {
      const chunk = buffer.slice(offset, offset + CHUNK_SIZE);
      connRef.current.send({ type: "file-chunk", chunk });
      offset += chunk.byteLength;

      const progress = Math.round((offset / buffer.byteLength) * 100);
      setSendingFile({ name: file.name, progress });

      await new Promise((res) => setTimeout(res, 10));
    }

    connRef.current.send({ type: "file-end" });

    const url = URL.createObjectURL(file);
    setMessages((prev) => [
      ...prev,
      {
        kind: "file",
        from: "me",
        name: file.name,
        size: file.size,
        fileType: file.type,
        url,
      },
    ]);

    setSendingFile(null);
    fileInputRef.current.value = "";
  };

  const getStream = async (type) => {
    return await navigator.mediaDevices.getUserMedia({
      video: type === "video",
      audio: true,
    });
  };

  const attachStream = (ref, stream) => {
    console.log("attachStream called", {
      refExists: !!ref.current,
      streamId: stream?.id,
      audioTracks: stream?.getAudioTracks().length,
      videoTracks: stream?.getVideoTracks().length,
    });

    if (!ref.current) {
      console.error("ref is null — element not mounted yet");
      return;
    }

    ref.current.srcObject = stream;
    ref.current.volume = 1;
    ref.current.muted = false;
    ref.current
      .play()
      .then(() => console.log("play() succeeded", ref.current))
      .catch((err) => console.error("play() failed", err));
  };

  const startCall = async (type) => {
    setCallType(type);
    setCallStatus("calling");

    const stream = await getStream(type);
    localStreamRef.current = stream;
    if (localVideoRef.current) localVideoRef.current.srcObject = stream;

    const call = peerRef.current.call(
      isHost ? connRef.current.peer : roomId,
      stream,
      { metadata: { type } }
    );

    callRef.current = call;

    call.on("stream", (remoteStream) => {
      console.log("stream event fired", {
        type,
        remoteStream,
        audioTracks: remoteStream.getAudioTracks(),
        videoTracks: remoteStream.getVideoTracks(),
      });
      remoteStreamRef.current = remoteStream;
      setCallStatus("ongoing");

      if (callType === "video") {
        attachStream(remoteVideoRef, remoteStream);
      }
      attachStream(remoteAudioRef, remoteStream);
    });
  };

  const answerCall = async () => {
    const call = incomingCall;
    if (!call) return;

    const stream = await getStream(callType);
    localStreamRef.current = stream;
    if (localVideoRef.current) localVideoRef.current.srcObject = stream;

    call.on("stream", (remoteStream) => {
      console.log("stream event fired", {
        callType,
        remoteStream,
        audioTracks: remoteStream.getAudioTracks(),
        videoTracks: remoteStream.getVideoTracks(),
      });
      remoteStreamRef.current = remoteStream;
      setCallStatus("ongoing");
      if (callType === "video") {
        attachStream(remoteVideoRef, remoteStream);
      }
      attachStream(remoteAudioRef, remoteStream);
    });

    call.on("error", (err) => console.error("Answer error:", err));
    call.on("close", () => setCallStatus("idle"));

    call.answer(stream);
    callRef.current = call;
    setCallStatus("ongoing");
    setIncomingCall(null);
  };

  const rejectCall = () => {
    connRef.current?.send({ type: "call-rejected" });
    incomingCall?.close();
    setIncomingCall(null);
    setCallStatus("idle");
    setCallType(null);
  };

  const endCall = () => {
    connRef.current?.send({ type: "call-ended" });
    callRef.current?.close();
    callRef.current = null;
    stopLocalStream();
    setCallStatus("idle");
    setCallType(null);
    setIsMuted(false);
    setIsCamOff(false);
  };

  const stopLocalStream = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
  };

  const toggleMute = () => {
    const audio = localStreamRef.current?.getAudioTracks()[0];
    if (!audio) return;
    audio.enabled = !audio.enabled;
    setIsMuted(!isMuted);
  };

  const toggleCamera = () => {
    const video = localStreamRef.current?.getVideoTracks()[0];
    if (!video) return;
    video.enabled = !video.enabled;
    setIsCamOff(!isCamOff);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const closeRoom = () => {
    connRef.current?.close();
    peerRef.current?.destroy();
    navigate("/ticket-list");
  };

  const roomLink = `${roomId}`;

  if (!iceServers || status === "idle")
    return (
      <Segment basic className="m-2-b">
        <Container fluid>
          <p>در حال آماده سازی اتصال...</p>
        </Container>
      </Segment>
    );

  if (status === "waiting_for_host") 
    return (
      <div className="waiting-overlay">
        <p>در انتظار اتصال میزبان...</p>
        <p className="attempt-count">
          تلاش {hostAttempt} از {HOST_MAX_ATTEMPTS}
        </p>
      </div>
    )

  if (status === "connecting")
    return (
      <Segment basic className="m-2-b">
        <Container fluid>
          <p>در حال برقراری ارتباط ...</p>
        </Container>
      </Segment>
    );
  if (status === "error")
    return (
      <Segment basic className="m-2-b">
        <Container fluid>
          <p>❌ اتصال برقرار نشد. چت وجود ندارد.</p>
          <p style={{ color: "#888", fontSize: 12 }}>{errorMessage}</p>
        </Container>
      </Segment>
    );
  if (status === "disconnected" && !isHost)
    return (
      <Segment basic className="m-2-b">
        <Container fluid>
          <p>{theirName ?? "طرف مقابل"} اتاق را ترک کرده است. منتظر بمانید.</p>
        </Container>
      </Segment>
    );

  const CopyLink = () => {
    if (navigator.clipboard && window.isSecureContext) {
      ServiceNotification.show("", "لینک کپی شد.", "success");
      let base = window.location.origin;
      return navigator.clipboard.writeText(`${base}/public/chat/${roomLink}`);
    }

    const textarea = document.createElement("textarea");
    textarea.value = roomLink;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
  };

  if (status === "waiting") {
    return (
      <Segment basic className="m-2-b">
        <Container fluid>
          <p>لینک زیر را برای دوستتان بفرستید</p>
          {/* <code className="ui label">{roomLink}</code> */}
          <Input
            fluid
            readOnly
            value={`${window.location.origin}/public/chat/${roomLink}`}
            action={
              <Button color="green" size="mini" onClick={() => CopyLink()}>
                کپی لینک
              </Button>
            }
          />
          <Divider />
          <p>منتظر ورود کاربر...</p>
        </Container>
      </Segment>
    );
  }

  return (
    <>
      {token ? (
        <Menu className="psubHeader">
          <Container fluid>
            <Button
              basic
              className="transparentBtn"
              onClick={() => {
                navigate("/chat-list");
              }}
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            <Header className="pageHeader">چت لحظه ای</Header>
            <Menu.Item position="right" className="py-0">
              <Button onClick={closeRoom} color="red" size="small">
                ترک چت
              </Button>
            </Menu.Item>
          </Container>
        </Menu>
      ) : null}

      <Segment
        basic
        className="m-2-b"
        style={{
          // maxWidth: `${token ? "100%" : "100%"}`,
          margin: `${token ? "auto" : "2em auto"}`,
        }}
      >
        <Container fluid>
          <div>
            <p>
              ✅ در حال چت با <b>{theirName ?? "..."}</b>
            </p>

            {callStatus === "receiving" && (
              <div
                style={{
                  background: "#f0f4ff",
                  border: "1px solid #aac",
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                <p>
                  📞 {theirName} در حال زنگ زدن است (
                  {callType === "video" ? "تماس تصویری" : "تماس صوتی"})
                </p>
                <Button
                  onClick={answerCall}
                  style={{ marginLeft: 8 }}
                  color="green"
                  size="small"
                >
                  پاسخ
                </Button>
                <Button onClick={rejectCall} color="red" size="small">
                  رد کردن
                </Button>
              </div>
            )}

            {callStatus === "calling" && (
              <div
                style={{
                  background: "#fffbe6",
                  border: "1px solid #dda",
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                <p>📞 در حال برقراری تماس...</p>
                <Button onClick={endCall} color="red" size="small">
                  قطع تماس
                </Button>
              </div>
            )}

            {callStatus === "ongoing" && (
              <div
                style={{
                  background: "#111",
                  borderRadius: 8,
                  padding: 8,
                  marginBottom: 8,
                }}
              >
                {callType === "video" && (
                  <div
                    style={{ position: "relative", display: "flex", gap: 8 }}
                  >
                    <video
                      ref={remoteVideoRef}
                      autoPlay
                      playsInline
                      style={{
                        width: "100%",
                        borderRadius: 6,
                        background: "#222",
                      }}
                      className="ui image large"
                    />
                    <video
                      ref={localVideoRef}
                      autoPlay
                      muted
                      playsInline
                      style={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        width: 120,
                        borderRadius: 6,
                        border: "2px solid #fff",
                        background: "#333",
                      }}
                    />
                  </div>
                )}
                {callType === "voice" && (
                  <div
                    style={{ textAlign: "center", padding: 24, color: "#fff" }}
                  >
                    <p style={{ fontSize: 48 }}>🎙️</p>
                    <p>تماس صوتی در جریان است با {theirName}</p>
                  </div>
                )}

                <audio
                  ref={remoteAudioRef}
                  autoPlay
                  playsInline
                  style={{ display: "none" }}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 12,
                    marginTop: 8,
                  }}
                >
                  {isMuted ? (
                    <Button size="small" onClick={toggleMute}>
                      <FontAwesomeIcon icon={faVolumeMute} />
                      &nbsp; صدا خاموش
                    </Button>
                  ) : (
                    <Button size="small" onClick={toggleMute}>
                      <FontAwesomeIcon icon={faVolumeHigh} />
                      &nbsp; صدا روشن
                    </Button>
                  )}

                  {callType === "video" &&
                    (isCamOff ? (
                      <Button size="small" onClick={toggleCamera}>
                        <FontAwesomeIcon icon={faVideoSlash} />
                        &nbsp; دوربین خاموش
                      </Button>
                    ) : (
                      <Button size="small" onClick={toggleCamera}>
                        <FontAwesomeIcon icon={faVideoCamera} />
                        &nbsp; دوربین روشن
                      </Button>
                    ))}
                  <Button size="small" onClick={endCall} color="red">
                    <FontAwesomeIcon icon={faPhoneSlash} />
                    &nbsp; پایان تماس
                  </Button>
                </div>
              </div>
            )}

            <div
              style={{
                height: 300,
                overflowY: "auto",
                padding: 8,
                borderRadius: "4px",
                background: "#fff",
              }}
            >
              {messages.length === 0 && (
                <p style={{ color: "#aaa" }}>هنوز پیامی ارسال نشده...</p>
              )}
              {messages.map((m, i) => (
                <div
                  className="d-flex ai-center w-100"
                  key={i}
                  style={{
                    flexFlow: m.from === "me" ? "row" : "row-reverse",
                    textAlign: m.from === "me" ? "right" : "left",
                    margin: "4px 0",
                  }}
                >
                  {m.kind === "text" && (
                    <span
                      className={m.from === "me" ? "mybubble" : "theirbubble"}
                      style={{ margin: "0 1em" }}
                    >
                      {m.text}
                    </span>
                  )}

                  {m.kind === "file" && (
                    <span
                      className={
                        m.from === "me" ? "mybubble-file" : "theirbubble-file"
                      }
                      style={{ margin: "0 1em" }}
                    >
                      <FontAwesomeIcon icon={faPaperclip} /> &nbsp;
                      <a
                        style={{ color: "#000" }}
                        href={m.url}
                        download={m.name}
                      >
                        {m.name}
                      </a>
                    </span>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <Divider hidden />

            <Input
              fluid
              style={{ margin: "1em 0" }}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="پیام خود را بنویسید..."
              action={
                <Button primary onClick={sendMessage}>
                  ارسال
                </Button>
              }
            />

            {callStatus === "idle" && (
              <>
                <Button
                  size="small"
                  onClick={() => startCall("voice")}
                  title="تماس صوتی"
                >
                  <FontAwesomeIcon icon={faPhone} /> &nbsp; تماس صوتی
                </Button>
                <Button
                  size="small"
                  onClick={() => startCall("video")}
                  title="تماس تصویری"
                >
                  <FontAwesomeIcon icon={faVideoCamera} /> &nbsp; تماس تصویری
                </Button>
              </>
            )}

            <Button size="small" onClick={() => fileInputRef.current.click()}>
              <FontAwesomeIcon icon={faPaperclip} /> &nbsp; ارسال فایل
            </Button>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={sendFile}
              />

              {/* File transfer progress */}
              {sendingFile && (
                <p style={{ color: "#555" }}>
                  📤 در حال ارسال {sendingFile.name}... {sendingFile.progress}%
                </p>
              )}
              {receivingFile && (
                <p style={{ color: "#555" }}>
                  📥 در حال دریافت {receivingFile.name}...{" "}
                  {receivingFile.progress}%
                </p>
              )}
            </div>
          </div>
        </Container>
      </Segment>
    </>
  );
};

export default ChatRoom;
